import { textToSpeechService } from './textToSpeechService';
import { isDictOfflineEnabled } from './dictionaryOfflineService';
import { translationService } from './translationService';

export interface RichDictionaryTranslation {
  word: string;
  reverse_translations?: string[];
  frequency_bars?: number;
  frequency_score?: number;
}

export interface RichDictionaryPosGroup {
  pos: string;
  translations: RichDictionaryTranslation[];
}

export interface RichDictionaryDefinition {
  pos: string;
  definition: string;
  example?: string;
}

export interface RichDictionaryItem {
  word: string;
  primary_translation: string;
  translations: string[];
  frequency_score?: number;
  level?: string;
  dictionary?: RichDictionaryPosGroup[];
  definitions?: RichDictionaryDefinition[];
  examples?: string[];
  id?: number | string;
}

export interface ResolvedWordTranslation {
  word: string;
  primaryTranslation: string;
  translations: string[];
  sourceLang: string;
  targetLang: string;
  richItem?: RichDictionaryItem;
  source: 'rich-r2' | 'google' | 'legacy-r2';
}

const R2_BASE_PRIMARY = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/new-dictionary-flashcards';
const R2_BASE_SECONDARY = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-backup/new-dictionary-flashcards';

const langNameMap: Record<string, string> = {
  pt: 'portuguese',
  en: 'english',
  es: 'spanish',
  fr: 'french',
  it: 'italian',
  de: 'german',
  ru: 'russian',
  hu: 'hungarian',
  zh: 'chinese',
  ja: 'japanese',
  nl: 'dutch',
  portugues: 'portuguese',
  portuguese: 'portuguese',
  ingles: 'english',
  english: 'english',
  espanhol: 'spanish',
  spanish: 'spanish',
  frances: 'french',
  french: 'french',
  italiano: 'italian',
  italian: 'italian',
  alemao: 'german',
  german: 'german',
  russo: 'russian',
  russian: 'russian',
  hungaro: 'hungarian',
  hungarian: 'hungarian',
  chines: 'chinese',
  chinese: 'chinese',
  japones: 'japanese',
  japanese: 'japanese',
  holandes: 'dutch',
  dutch: 'dutch'
};

const loadedDictsMemory = new Map<string, Map<string, RichDictionaryItem>>();
const pendingFetches = new Map<string, Promise<Map<string, RichDictionaryItem> | null>>();

export const resolveLanguageName = (codeOrName?: string): string => {
  if (!codeOrName) return 'english';
  const clean = codeOrName.trim().toLowerCase().split('-')[0].normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return langNameMap[clean] || clean;
};

export const getDictionaryCandidatePaths = (sourceLang: string, targetLang: string): string[] => {
  const src = resolveLanguageName(sourceLang);
  const tgt = resolveLanguageName(targetLang);

  if (src === tgt) return [];

  const supported = new Set([
    'portuguese', 'english', 'spanish', 'french', 'italian',
    'german', 'russian', 'hungarian', 'chinese', 'japanese', 'dutch'
  ]);
  if (!supported.has(src) || !supported.has(tgt)) {
    return [];
  }

  const fileName = `${src}_to_${tgt}.json`;
  return Array.from(new Set([
    `${src}/${fileName}`,
    `${tgt}/${fileName}`,
    fileName
  ]));
};

export const getDictionaryFileName = (sourceLang: string, targetLang: string): string | null => {
  const src = resolveLanguageName(sourceLang);
  const tgt = resolveLanguageName(targetLang);

  if (src === tgt) return null;

  const supported = new Set([
    'portuguese', 'english', 'spanish', 'french', 'italian',
    'german', 'russian', 'hungarian', 'chinese', 'japanese', 'dutch'
  ]);
  if (!supported.has(src) || !supported.has(tgt)) {
    return null;
  }

  return `${src}_to_${tgt}.json`;
};

const fetchAndIndexDictionaryFile = async (sourceLang: string, targetLang: string): Promise<Map<string, RichDictionaryItem> | null> => {
  const fileName = getDictionaryFileName(sourceLang, targetLang);
  if (!fileName) return null;

  if (loadedDictsMemory.has(fileName)) {
    return loadedDictsMemory.get(fileName)!;
  }

  if (pendingFetches.has(fileName)) {
    return pendingFetches.get(fileName)!;
  }

  const candidatePaths = getDictionaryCandidatePaths(sourceLang, targetLang);
  if (candidatePaths.length === 0) return null;

  const fetchPromise = (async (): Promise<Map<string, RichDictionaryItem> | null> => {
    try {
      let data: RichDictionaryItem[] | null = null;

      // 1. Try CacheStorage
      if (typeof window !== 'undefined' && 'caches' in window && isDictOfflineEnabled()) {
        try {
          const cache = await caches.open('rich-dict-cache-v2');
          for (const cand of candidatePaths) {
            const primaryUrl = `${R2_BASE_PRIMARY}/${cand}`;
            const cachedRes = await cache.match(primaryUrl);
            if (cachedRes && cachedRes.ok) {
              data = await cachedRes.json();
              if (data) break;
            }
          }
        } catch (e) {}
      }

      // 2. Fetch network if not cached
      if (!data) {
        for (const cand of candidatePaths) {
          const primaryUrl = `${R2_BASE_PRIMARY}/${cand}`;
          const secondaryUrl = `${R2_BASE_SECONDARY}/${cand}`;

          try {
            let response = await fetch(primaryUrl);
            if (!response.ok) {
              response = await fetch(secondaryUrl);
            }
            if (response.ok) {
              const cloneForCache = response.clone();
              data = await response.json();
              if (typeof window !== 'undefined' && 'caches' in window && data && isDictOfflineEnabled()) {
                try {
                  const cache = await caches.open('rich-dict-cache-v2');
                  await cache.put(primaryUrl, cloneForCache);
                } catch (e) {}
              }
              if (data) break;
            }
          } catch (fetchErr) {
            // continue trying next candidate
          }
        }
      }

      if (data && Array.isArray(data)) {
        const itemMap = new Map<string, RichDictionaryItem>();
        for (const item of data) {
          if (item && item.word) {
            const key = item.word.toLowerCase().trim();
            if (!itemMap.has(key)) {
              itemMap.set(key, item);
            }
          }
        }
        loadedDictsMemory.set(fileName, itemMap);
        return itemMap;
      }
    } catch (err) {
      console.warn(`[RichDictionary] Failed to load ${fileName}:`, err);
    } finally {
      pendingFetches.delete(fileName);
    }
    return null;
  })();

  pendingFetches.set(fileName, fetchPromise);
  return fetchPromise;
};

export const lookupRichDictionary = async (
  word: string,
  sourceLang: string,
  targetLang: string
): Promise<RichDictionaryItem | null> => {
  const dictMap = await fetchAndIndexDictionaryFile(sourceLang, targetLang);
  if (!dictMap) return null;

  const cleanWord = word.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\"']/g, "").trim();
  if (!cleanWord) return null;

  // 1. Exact match
  if (dictMap.has(cleanWord)) {
    return dictMap.get(cleanWord)!;
  }

  // 2. Inflection fallbacks
  const fallbacks: string[] = [];
  if (cleanWord.endsWith('ies')) {
    fallbacks.push(cleanWord.slice(0, -3) + 'y');
    fallbacks.push(cleanWord.slice(0, -3) + 'ie');
  }
  if (cleanWord.endsWith('es')) {
    fallbacks.push(cleanWord.slice(0, -2));
    fallbacks.push(cleanWord.slice(0, -1));
  }
  if (cleanWord.endsWith('s')) {
    fallbacks.push(cleanWord.slice(0, -1));
  }
  if (cleanWord.endsWith('ed')) {
    fallbacks.push(cleanWord.slice(0, -2));
    fallbacks.push(cleanWord.slice(0, -1));
  }
  if (cleanWord.endsWith('ing')) {
    fallbacks.push(cleanWord.slice(0, -3));
    fallbacks.push(cleanWord.slice(0, -3) + 'e');
  }

  for (const fb of fallbacks) {
    if (dictMap.has(fb)) {
      return dictMap.get(fb)!;
    }
  }

  return null;
};

/**
 * Executes the required 3-level fallback hierarchy:
 * 1. Rich Dictionary (R2 Cloudflare - Novo)
 * 2. Google Translate API Free (Existente no app)
 * 3. free-dict legado (R2 Cloudflare)
 */
export const resolveWordTranslationHierarchy = async (
  word: string,
  targetLang: string,
  sourceLang: string = 'en'
): Promise<ResolvedWordTranslation> => {
  const cleanWord = word.trim();

  // Level 1: Rich Dictionary (R2 Cloudflare - Novo)
  try {
    const richItem = await lookupRichDictionary(cleanWord, sourceLang, targetLang);
    if (richItem && richItem.primary_translation) {
      return {
        word: richItem.word || cleanWord,
        primaryTranslation: richItem.primary_translation,
        translations: richItem.translations || [richItem.primary_translation],
        sourceLang,
        targetLang,
        richItem,
        source: 'rich-r2'
      };
    }
  } catch (err) {
    console.warn('[RichDictionary] Level 1 fallback error:', err);
  }

  // Level 2: Google Translate API Free (Existente no app)
  try {
    const googleRes = await translationService.unifiedTranslate(cleanWord, targetLang, undefined, sourceLang, undefined, false, false, { isSingleWord: true });
    if (googleRes && googleRes.translation && googleRes.translation.toLowerCase().trim() !== cleanWord.toLowerCase().trim()) {
      return {
        word: cleanWord,
        primaryTranslation: googleRes.translation,
        translations: [googleRes.translation],
        sourceLang,
        targetLang,
        source: 'google'
      };
    }
  } catch (err) {
    console.warn('[RichDictionary] Level 2 fallback error:', err);
  }

  // Level 3: free-dict legado (R2 Cloudflare)
  try {
    const legacyRes = await translationService.translateWordWithFallback(cleanWord, targetLang, sourceLang);
    if (legacyRes && legacyRes.translation) {
      return {
        word: cleanWord,
        primaryTranslation: legacyRes.translation,
        translations: [legacyRes.translation],
        sourceLang,
        targetLang,
        source: 'legacy-r2'
      };
    }
  } catch (err) {
    console.warn('[RichDictionary] Level 3 fallback error:', err);
  }

  return {
    word: cleanWord,
    primaryTranslation: cleanWord,
    translations: [cleanWord],
    sourceLang,
    targetLang,
    source: 'legacy-r2'
  };
};
